import React from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableHead, TableRow, Paper, Chip } from '@mui/material';

// ============================================================
// AI実装プランの JSONB スキーマ定義
// ============================================================

export type Mark =
  | 'bold'
  | 'italic'
  | 'underline'
  | 'strike'
  | 'code'
  | `color:${string}`
  | `highlight:${string}`;

export interface Span {
  text: string;
  marks?: Mark[];
}

export type CalloutVariant = 'info' | 'success' | 'warning' | 'danger' | 'note';

export type Block =
  | { type: 'heading'; level: 1 | 2 | 3; text: string }
  | { type: 'paragraph'; spans: Span[] }
  | { type: 'list'; ordered: boolean; items: { spans: Span[] }[] }
  | { type: 'table'; headers: string[]; rows: string[][] }
  | { type: 'code'; language?: string; text: string }
  | { type: 'callout'; variant: CalloutVariant; spans: Span[] }
  | { type: 'key_value'; items: { key: string; value: string }[] }
  | { type: 'divider' };

export interface AiPlanContent {
  schema_version: string;
  blocks: Block[];
}

// ============================================================
// インライン span のレンダリング
// ============================================================

const SpanView: React.FC<{ span: Span }> = ({ span }) => {
  const style: React.CSSProperties = {};
  let isCode = false;

  (span.marks || []).forEach((mark) => {
    if (mark === 'bold') style.fontWeight = 'bold';
    else if (mark === 'italic') style.fontStyle = 'italic';
    else if (mark === 'underline') style.textDecoration = 'underline';
    else if (mark === 'strike') style.textDecoration = 'line-through';
    else if (mark === 'code') isCode = true;
    else if (mark.startsWith('color:')) style.color = mark.slice('color:'.length);
    else if (mark.startsWith('highlight:')) style.backgroundColor = mark.slice('highlight:'.length);
  });

  if (isCode) {
    return (
      <Box
        component="code"
        sx={{
          ...style,
          px: 0.75,
          py: 0.25,
          mx: 0.25,
          bgcolor: '#f1f5f9',
          color: '#b91c1c',
          borderRadius: 0.5,
          fontFamily: 'Consolas, Menlo, monospace',
          fontSize: '0.875em',
        }}
      >
        {span.text}
      </Box>
    );
  }

  return <span style={style}>{span.text}</span>;
};

const SpansView: React.FC<{ spans: Span[] }> = ({ spans }) => (
  <>
    {spans.map((s, i) => (
      <SpanView key={i} span={s} />
    ))}
  </>
);

// ============================================================
// 各ブロックのレンダリング
// ============================================================

const calloutStyles: Record<CalloutVariant, { bg: string; border: string; color: string }> = {
  info: { bg: '#e3f2fd', border: '#1976d2', color: '#0d47a1' },
  success: { bg: '#e8f5e9', border: '#2e7d32', color: '#1b5e20' },
  warning: { bg: '#fff8e1', border: '#f57c00', color: '#e65100' },
  danger: { bg: '#ffebee', border: '#c62828', color: '#b71c1c' },
  note: { bg: '#f5f5f5', border: '#616161', color: '#212121' },
};

const BlockView: React.FC<{ block: Block }> = ({ block }) => {
  switch (block.type) {
    case 'heading': {
      const variant = block.level === 1 ? 'h5' : block.level === 2 ? 'h6' : 'subtitle1';
      return (
        <Typography variant={variant} sx={{ fontWeight: 700, mt: 2, mb: 1 }}>
          {block.text}
        </Typography>
      );
    }

    case 'paragraph':
      return (
        <Typography variant="body2" sx={{ my: 1, lineHeight: 1.8 }}>
          <SpansView spans={block.spans} />
        </Typography>
      );

    case 'list': {
      const ListTag = block.ordered ? 'ol' : 'ul';
      return (
        <Box component={ListTag} sx={{ my: 1, pl: 3 }}>
          {block.items.map((item, i) => (
            <Box component="li" key={i} sx={{ my: 0.5 }}>
              <Typography variant="body2" component="span" sx={{ lineHeight: 1.8 }}>
                <SpansView spans={item.spans} />
              </Typography>
            </Box>
          ))}
        </Box>
      );
    }

    case 'table':
      return (
        <Paper variant="outlined" sx={{ my: 1.5, overflow: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                {block.headers.map((h, i) => (
                  <TableCell key={i} sx={{ fontWeight: 700 }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {block.rows.map((row, ri) => (
                <TableRow key={ri}>
                  {row.map((cell, ci) => (
                    <TableCell key={ci}>{cell}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      );

    case 'code':
      return (
        <Box
          component="pre"
          sx={{
            my: 1.5,
            p: 1.5,
            bgcolor: '#0f172a',
            color: '#e2e8f0',
            borderRadius: 1,
            overflow: 'auto',
            fontFamily: 'Consolas, Menlo, monospace',
            fontSize: '0.85rem',
          }}
        >
          {block.language && (
            <Chip
              label={block.language}
              size="small"
              sx={{ mb: 1, bgcolor: '#334155', color: '#cbd5e1', fontSize: '0.7rem' }}
            />
          )}
          <Box component="code" sx={{ display: 'block', whiteSpace: 'pre' }}>
            {block.text}
          </Box>
        </Box>
      );

    case 'callout': {
      const s = calloutStyles[block.variant];
      return (
        <Box
          sx={{
            my: 1.5,
            p: 1.5,
            bgcolor: s.bg,
            color: s.color,
            borderLeft: `4px solid ${s.border}`,
            borderRadius: 1,
          }}
        >
          <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
            <SpansView spans={block.spans} />
          </Typography>
        </Box>
      );
    }

    case 'key_value':
      return (
        <Box sx={{ my: 1.5, display: 'grid', gridTemplateColumns: '180px 1fr', gap: 0.5 }}>
          {block.items.map((kv, i) => (
            <React.Fragment key={i}>
              <Box sx={{ p: 1, bgcolor: '#f1f5f9', fontWeight: 600, fontSize: '0.85rem' }}>{kv.key}</Box>
              <Box sx={{ p: 1, borderBottom: '1px solid #e0e0e0', fontSize: '0.85rem' }}>{kv.value}</Box>
            </React.Fragment>
          ))}
        </Box>
      );

    case 'divider':
      return <Box sx={{ my: 2, borderTop: '1px solid #e0e0e0' }} />;

    default:
      // 未知のブロック型はフォールバック表示（スキーマ拡張時の前方互換）
      return (
        <Box sx={{ my: 1, p: 1, bgcolor: '#fafafa', border: '1px dashed #ccc', fontSize: '0.75rem' }}>
          <Typography variant="caption" color="text.secondary">
            未対応のブロック形式
          </Typography>
          <Box component="pre" sx={{ m: 0, fontSize: '0.7rem' }}>
            {JSON.stringify(block, null, 2)}
          </Box>
        </Box>
      );
  }
};

// ============================================================
// メイン: コンテンツ全体のレンダリング
// ============================================================

interface AiPlanBlockRendererProps {
  content: AiPlanContent | null | undefined;
}

const AiPlanBlockRenderer: React.FC<AiPlanBlockRendererProps> = ({ content }) => {
  if (!content || !Array.isArray(content.blocks)) {
    return (
      <Typography variant="body2" color="text.secondary">
        コンテンツが空です
      </Typography>
    );
  }

  return (
    <Box>
      {content.blocks.map((block, i) => (
        <BlockView key={i} block={block} />
      ))}
    </Box>
  );
};

export default AiPlanBlockRenderer;
